import type { AbMoney, LineItem, LineItemProduct } from '@ab-core/graphql/dist';
import { usePositionPriceObject, useProductsAvailabilitiesWithChannel } from '@ab-core/hooks-v2';
import type { CartOffer } from '@ab-core/hooks/cart/useCartOffer';
import CreateOrUpdateCustomLineItem from '@ab-core/hooks/cartNew/LineItems/addCustomLineItemToCart/createOrUpdateCusomLineItem';
import AddLineItem from '@ab-core/hooks/cartNew/LineItems/addToCart/addToCart';
import UpdateLineItem from '@ab-core/hooks/cartNew/LineItems/addToCart/updateLineItem';
import DeleteCustomLineItem from '@ab-core/hooks/cartNew/LineItems/deleteFromCart/deleteCustomLineItem';
import DeleteFromCart from '@ab-core/hooks/cartNew/LineItems/deleteFromCart/deleteFromCart';
import type { MappedCart } from '@ab-core/hooks/cartNew/mappers/cartMapper';
import { cartStore } from '@ab-core/store';
import { toast } from '@ab-core/toast';
import { useEffect, useMemo, useRef } from 'react';

type SpecialShippingManagerParams = {
    cartId?: string;
    lineItems?: Array<LineItemProduct>;
    postalCode?: string;
};

export enum SPECIAL_SHIPPING_COSTS_TYPE {
    PV = 'PV',
    GG = 'GG'
}

export const SPECIAL_SHIPPING_SKUS = {
    [SPECIAL_SHIPPING_COSTS_TYPE.PV]: '0989031',
    [SPECIAL_SHIPPING_COSTS_TYPE.GG]: '0989029'
};

const ALL_SPECIAL_SHIPPING_SKUS = Object.values(SPECIAL_SHIPPING_SKUS);

export function isSpecialShippingLineItem(lineItem: LineItemProduct): boolean {
    return ALL_SPECIAL_SHIPPING_SKUS.includes(lineItem.sku || '');
}

function getProductSpecialShippingType(
    lineItem: LineItemProduct,
    deliveryFromFactorySkus?: Set<string>
): SPECIAL_SHIPPING_COSTS_TYPE | null {
    if (isSpecialShippingLineItem(lineItem)) {
        return null;
    }

    if (lineItem.photovoltaicProduct === '1') {
        return SPECIAL_SHIPPING_COSTS_TYPE.PV;
    }

    if (lineItem.isDangerousGoodsForwarding === true && !deliveryFromFactorySkus?.has(lineItem.sku || '')) {
        return SPECIAL_SHIPPING_COSTS_TYPE.GG;
    }

    return null;
}

export function getRequiredSpecialShippingTypes(
    lineItems: Array<LineItemProduct> | undefined,
    deliveryFromFactorySkus?: Set<string>
): Array<SPECIAL_SHIPPING_COSTS_TYPE> {
    if (!lineItems) {
        return [];
    }

    const types = new Set<SPECIAL_SHIPPING_COSTS_TYPE>();

    for (const lineItem of lineItems) {
        const type = getProductSpecialShippingType(lineItem, deliveryFromFactorySkus);

        if (type) {
            types.add(type);
        }
    }

    return Array.from(types);
}

export const useGetFactoryDeliverySkus = (lineItems?: Array<LineItemProduct>, cartId?: string) => {
    const skus = useMemo(
        () => (lineItems?.map((item) => item.sku).filter(Boolean) as Array<string>) ?? [],
        [lineItems]
    );

    const { productsAvailabilitiesWithChannel } = useProductsAvailabilitiesWithChannel(
        { skus, cartId: cartId || '' },
        !cartId || skus.length === 0
    );

    return useMemo(() => {
        const set = new Set<string>();

        productsAvailabilitiesWithChannel?.forEach((availability) => {
            if (availability.deliveryFromFactory && availability.sku) {
                set.add(availability.sku);
            }
        });

        return set;
    }, [productsAvailabilitiesWithChannel]);
};

export const useSpecialShippingManager = ({ cartId, lineItems, postalCode }: SpecialShippingManagerParams) => {
    const { addLineItem, pending: addPending } = AddLineItem(cartId || '');
    const { deleteFromCart: deleteSpecialShipping, pending: deletePending } = DeleteFromCart();
    const pendingRef = useRef(false);

    const deliveryFromFactorySkus = useGetFactoryDeliverySkus(lineItems, cartId);

    const { toAdd, toRemove, existingItems } = useMemo(() => {
        if (!lineItems) {
            return { toAdd: [], toRemove: [], existingItems: [] };
        }

        const requiredTypes = getRequiredSpecialShippingTypes(lineItems, deliveryFromFactorySkus);
        const requiredSkus = requiredTypes.map((type) => SPECIAL_SHIPPING_SKUS[type]);
        const existing = lineItems.filter(isSpecialShippingLineItem);
        const existingSkus = existing.map((li) => li.sku || '');

        return {
            toAdd: requiredSkus.filter((sku) => !existingSkus.includes(sku)),
            toRemove: existing
                .filter((li) => !requiredSkus.includes(li.sku || ''))
                .map((li) => ({ id: li.id, sku: li.sku || '' })),
            existingItems: existing
        };
    }, [lineItems, deliveryFromFactorySkus]);

    useEffect(() => {
        if (!cartId || pendingRef.current || addPending || deletePending) {
            return;
        }

        if (toAdd.length > 0) {
            pendingRef.current = true;
            addLineItem({
                variables: {
                    cartIds: cartId,
                    lineItemInput: toAdd.map((sku) => ({ sku, quantity: 1 }))
                }
            });
        }
    }, [cartId, toAdd, addPending, deletePending]);

    useEffect(() => {
        if (!cartId || pendingRef.current || addPending || deletePending) {
            return;
        }

        if (toRemove.length > 0) {
            pendingRef.current = true;
            deleteSpecialShipping({
                variables: {
                    cartId,
                    lineItemIds: toRemove.map((item) => item.id)
                }
            });
        }
    }, [cartId, toRemove, addPending, deletePending]);

    useEffect(() => {
        if (!addPending && !deletePending) {
            pendingRef.current = false;
        }
    }, [addPending, deletePending]);

    const hasSpecialShippingCosts = useMemo(
        () => getRequiredSpecialShippingTypes(lineItems, deliveryFromFactorySkus).length > 0,
        [lineItems, deliveryFromFactorySkus]
    );

    const hasPvItem = existingItems.some((i) => i.sku === SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.PV]);
    const hasGgItem = existingItems.some((i) => i.sku === SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.GG]);

    const { positionPriceObject: pvPrice, loading: pvLoading } = usePositionPriceObject({
        productId: SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.PV],
        quantity: 1,
        pvggType: SPECIAL_SHIPPING_COSTS_TYPE.PV,
        skip: !hasPvItem,
        postalCode
    });

    const { positionPriceObject: ggPrice, loading: ggLoading } = usePositionPriceObject({
        productId: SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.GG],
        quantity: 1,
        pvggType: SPECIAL_SHIPPING_COSTS_TYPE.GG,
        skip: !hasGgItem
    });

    const specialShippingCostPrices: Record<string, AbMoney | null | undefined> = {
        [SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.PV]]: pvPrice?.value,
        [SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.GG]]: ggPrice?.value
    };

    return {
        hasSpecialShippingCosts,
        specialShippingCostPrices,
        existingSpecialShippingItems: existingItems,
        loading: pvLoading || ggLoading
    };
};

export const useLineItemsUpdates = (cartOfferUnion: MappedCart | CartOffer, firstLoading: boolean) => {
    const { updateLineItem } = UpdateLineItem();

    const { deleteFromCart, success: deleteItemSuccess } = DeleteFromCart();
    const { createOrUpdateCustomLineItem } = CreateOrUpdateCustomLineItem();
    const { deleteCustomLineItem, success: deleteCLISuccess } = DeleteCustomLineItem();

    const handleDelete = (lineItem: LineItemProduct) => {
        if (firstLoading) {
            return;
        }

        deleteFromCart({
            variables: {
                cartId: cartOfferUnion?.id,
                lineItemIds: [lineItem.id]
            }
        });
        cartStore.setters.setLineItemDeleted(true);
    };
    const updateQuantity = (quantity: number, lineItem: LineItemProduct) => {
        if (firstLoading) {
            return;
        }

        if (quantity === lineItem.quantity) {
            return;
        }

        updateLineItem({
            variables: {
                cartIds: cartOfferUnion?.id,
                lineItemInput: { quantity, sku: lineItem.sku, id: lineItem.id }
            },
            onCompleted: () => {
                toast.success('Artikel aktualisiert');
            }
        });
    };
    const updatePostionText = (positionText: string, lineItem: LineItemProduct) => {
        if (firstLoading) {
            return;
        }

        if (positionText === lineItem.positionText) {
            return;
        }

        updateLineItem({
            variables: {
                cartIds: cartOfferUnion?.id,
                lineItemInput: {
                    fields: [
                        {
                            name: 'lineitem-positionstext',
                            value: String(positionText)
                        }
                    ],
                    quantity: lineItem.quantity,
                    sku: lineItem.sku,
                    id: lineItem.id
                }
            },
            onCompleted: () => {
                toast.success('Artikel aktualisiert');
            }
        });
    };
    const updateCustomLineItemPositionText = (positionText: string, customLineItem: LineItem) => {
        if (firstLoading) {
            return;
        }

        createOrUpdateCustomLineItem({
            variables: {
                cartIds: cartOfferUnion?.id,
                customLineItemInput: {
                    id: customLineItem.id,
                    ean: customLineItem.ean,
                    name: customLineItem.name,
                    quantity: customLineItem.quantity,
                    quantityUnit: customLineItem.quantityUnit,
                    supplierName: customLineItem.supplierName,
                    supplierProductNumber: customLineItem.supplierProductNumber,
                    positionText
                }
            },
            onCompleted: () => {
                toast.success('Artikel aktualisiert');
            }
        });
    };
    const updateCustomLineItemQuantity = (quantity: number, customLineItem: LineItem) => {
        if (firstLoading) {
            return;
        }

        createOrUpdateCustomLineItem({
            variables: {
                cartIds: cartOfferUnion?.id,
                customLineItemInput: {
                    id: customLineItem.id,
                    ean: customLineItem.ean,
                    positionText: customLineItem.positionText,
                    name: customLineItem.name,
                    quantityUnit: customLineItem.quantityUnit,
                    supplierName: customLineItem.supplierName,
                    supplierProductNumber: customLineItem.supplierProductNumber,
                    quantity
                }
            },
            onCompleted: () => {
                toast.success('Artikel aktualisiert');
            }
        });
    };
    const handleDeleteCustomLineItem = (id: string) => {
        deleteCustomLineItem({
            variables: { cartId: cartOfferUnion?.id, lineItemIds: id }
        });
    };
    const updateNoCut = (noCut: boolean, lineItem: LineItemProduct) => {
        if (firstLoading) {
            return;
        }

        updateLineItem({
            variables: {
                cartIds: cartOfferUnion?.id,
                lineItemInput: {
                    fields: [
                        {
                            name: 'lineitem-nocut',
                            value: noCut ? '1' : '0'
                        }
                    ],
                    quantity: lineItem.quantity,
                    sku: lineItem.sku,
                    id: lineItem.id
                }
            },
            onCompleted: () => {
                toast.success('Artikel aktualisiert');
            }
        });
    };

    return {
        updateQuantity,
        handleDelete,
        handleDeleteCustomLineItem,
        updateCustomLineItemQuantity,
        updateCustomLineItemPositionText,
        updatePostionText,
        updateNoCut,
        deleteCLISuccess,
        deleteItemSuccess
    };
};
